import {
    Badge,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Table as ChakraTable,
    TableContainer,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { AgentNewsType } from '@olagg/db-types';
import Pagination from '@olagg/ui-kit/Pagination';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { BsSearch } from 'react-icons/bs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../../consts/routes';
import useAgentNews from '../hooks/useAgentNews';

const List = () => {
    const [newsList, setNewsList] = useState<AgentNewsType[]>([]);
    const [count, setCount] = useState(0);
    const { getAll, rejectNews } = useAgentNews();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
    const [rejectTarget, setRejectTarget] = useState<AgentNewsType | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectError, setRejectError] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);

    const [filter, setFilter] = useState({
        title: searchParams.get('title') || '',
        status: (searchParams.get('status') || '') as '' | 'draft' | 'published' | 'rejected',
        ai_generated: (searchParams.get('ai_generated') || '') as '' | 'true' | 'false',
        page: Number(searchParams.get('page') || 1),
        limit: Number(searchParams.get('limit') || 10),
    });

    const { page, limit, title, status, ai_generated } = filter;

    const loadNews = () =>
        getAll({ title: title || undefined, status: status || undefined, ai_generated: ai_generated !== '' ? ai_generated === 'true' : undefined, page, limit }).then((response) => {
            setNewsList(response.news);
            setCount(response.count);
        });

    const onFilter = ({ name, value }: EventTarget & HTMLInputElement) => {
        setFilter((prev) => ({ ...prev, [name]: value, page: name !== 'page' ? 1 : Number(value) }));

        if (value) searchParams.set(name, value);
        else searchParams.delete(name);
        if (name !== 'page') searchParams.delete('page');
        setSearchParams(searchParams);
    };

    useEffect(() => {
        loadNews();
    }, [filter]);

    const openRejectModal = (item: AgentNewsType) => {
        setRejectTarget(item);
        setRejectReason('');
        setRejectError('');
        onRejectOpen();
    };

    const handleReject = () => {
        if (!rejectTarget) return;
        const reason = rejectReason.trim();
        if (!reason) {
            setRejectError('El motivo es obligatorio');
            return;
        }
        setIsRejecting(true);
        rejectNews(rejectTarget.id, reason)
            .then(() => {
                toast({
                    title: 'Noticia rechazada',
                    status: 'success',
                    duration: 3000,
                    position: 'top-right',
                    isClosable: true,
                });
                onRejectClose();
                loadNews();
            })
            .catch((e: any) => {
                toast({
                    title: 'Error al rechazar: ' + (e?.errors?.join(', ') || e?.message || 'error desconocido'),
                    status: 'error',
                    duration: 4000,
                    position: 'top-right',
                    isClosable: true,
                });
            })
            .finally(() => setIsRejecting(false));
    };

    return (
        <Flex gap={5} direction="column" mt={4}>
            <Flex flexDirection="row" justify="space-between" pr={6} gap={2} mx={5}>
                <InputGroup w="40%">
                    <Input
                        color="white"
                        placeholder="Buscar por título"
                        name="title"
                        value={title}
                        onChange={(e) => onFilter(e.target)}
                    />
                    <InputRightElement children={<BsSearch color="white" />} />
                </InputGroup>
                <Flex gap={2}>
                    <Select
                        color="white"
                        w="200px"
                        name="status"
                        value={status}
                        onChange={(e) => onFilter(e.target as any)}
                        sx={{ option: { color: 'black' } }}
                    >
                        <option value="">Todos los estados</option>
                        <option value="draft">Borrador</option>
                        <option value="published">Publicado</option>
                        <option value="rejected">Rechazado</option>
                    </Select>
                    <Select
                        color="white"
                        w="200px"
                        name="ai_generated"
                        value={ai_generated}
                        onChange={(e) => onFilter(e.target as any)}
                        sx={{ option: { color: 'black' } }}
                    >
                        <option value="">Todos los orígenes</option>
                        <option value="true">Generado por IA</option>
                        <option value="false">Creado manualmente</option>
                    </Select>
                </Flex>
            </Flex>

            <Flex w="full" justifyContent="center" px="20px" pb="15px">
                <TableContainer overflowX="scroll" overflowY="scroll" w="100%" maxH="60vh">
                    <ChakraTable
                        variant="striped"
                        size="sm"
                        colorScheme="olaggPurple"
                        fontSize="18px"
                        position="relative"
                        whiteSpace="normal"
                    >
                        <Thead>
                            <Tr position="sticky" bg="gray.800" top="0" zIndex="100">
                                <Th py="20px" color="white">ID</Th>
                                <Th py="20px" color="white">Título</Th>
                                <Th py="20px" color="white">Categoría</Th>
                                <Th py="20px" color="white">Estado</Th>
                                <Th py="20px" color="white">Origen</Th>
                                <Th py="20px" color="white">Creado</Th>
                                <Th py="20px" color="white">Acciones</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {newsList?.map((item, index) => {
                                const statusLabel =
                                    item.status === 'published' ? 'Publicado'
                                        : item.status === 'rejected' ? 'Rechazado'
                                        : 'Borrador';
                                const statusColor =
                                    item.status === 'published' ? 'green'
                                        : item.status === 'rejected' ? 'red'
                                        : 'yellow';
                                return (
                                    <Tr key={index} color="white">
                                        <Td py="20px">{item.id}</Td>
                                        <Td py="20px" maxW="300px">{item.title}</Td>
                                        <Td py="20px">{item.main_category || item.category?.name || '-'}</Td>
                                        <Td py="20px">
                                            <Badge colorScheme={statusColor}>{statusLabel}</Badge>
                                        </Td>
                                        <Td py="20px">
                                            <Badge colorScheme={item.ai_generated ? 'purple' : 'gray'}>
                                                {item.ai_generated ? 'IA' : 'Manual'}
                                            </Badge>
                                        </Td>
                                        <Td py="20px">{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</Td>
                                        <Td py="20px">
                                            <Flex gap={2}>
                                                <Button
                                                    bgColor="#37A169"
                                                    p="0 10px"
                                                    h="30px"
                                                    fontSize="14px"
                                                    onClick={() => navigate(`/agent-news/${item.id}`)}
                                                >
                                                    {item.status === 'draft' ? 'Revisar' : 'Ver'}
                                                </Button>
                                                {item.status === 'draft' && (
                                                    <Button
                                                        colorScheme="red"
                                                        variant="outline"
                                                        p="0 10px"
                                                        h="30px"
                                                        fontSize="14px"
                                                        onClick={() => openRejectModal(item)}
                                                    >
                                                        Rechazar
                                                    </Button>
                                                )}
                                            </Flex>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </ChakraTable>
                </TableContainer>
            </Flex>

            <Flex px="20px" w="full" justifyContent="center">
                <Pagination
                    params={{ limit, page, count }}
                    onClick={(p) =>
                        onFilter({ name: 'page', value: String(p + 1) } as EventTarget & HTMLInputElement)
                    }
                    onLimitChange={(val) =>
                        onFilter({ name: 'limit', value: val } as EventTarget & HTMLInputElement)
                    }
                    hideLimitInput
                />
            </Flex>

            <Modal isOpen={isRejectOpen} onClose={onRejectClose} isCentered size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Rechazar noticia</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {rejectTarget && (
                            <Text fontSize="14px" mb={3} color="gray.600">
                                #{rejectTarget.id} — {rejectTarget.title}
                            </Text>
                        )}
                        <FormControl isInvalid={!!rejectError}>
                            <FormLabel>Motivo del rechazo</FormLabel>
                            <Textarea
                                value={rejectReason}
                                onChange={(e) => {
                                    setRejectReason(e.target.value);
                                    if (rejectError) setRejectError('');
                                }}
                                placeholder="Indicá por qué se rechaza esta noticia"
                                minH="140px"
                            />
                            {rejectError && (
                                <Text color="red.500" fontSize="sm" mt={1}>{rejectError}</Text>
                            )}
                        </FormControl>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={onRejectClose} isDisabled={isRejecting}>
                            Cancelar
                        </Button>
                        <Button colorScheme="red" onClick={handleReject} isLoading={isRejecting}>
                            Confirmar rechazo
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default List;
